import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './BottomSheet.css';

/**
 * BottomSheet — reusable mobile sheet pattern.
 *
 * A modal panel that rises from the bottom edge of the viewport instead of
 * centering like `.modal-overlay`/`.modal-content` (src/index.css). Built for
 * narrow-viewport consumers that currently render a desktop popover (starting
 * with CustomSelect) so small screens get large touch targets and a dismissal
 * gesture that matches the platform, instead of a shrunken floating box.
 *
 * Accessibility contract (mirrors the panel in Layout/NavOrganizer.jsx, the
 * only other hand-rolled dialog in this codebase — same reasoning applies: a
 * portaled dialog has to behave like a modal for keyboard + AT users):
 *   - `role="dialog"` + `aria-modal="true"`, labelled by `title` when given.
 *   - Focus moves into the sheet on open (`initialFocusRef`, else the first
 *     focusable element) and is trapped there — Tab/Shift+Tab cycle inside.
 *   - Escape closes. So does a tap on the backdrop, or dragging the grip
 *     down past a threshold.
 *   - On close, focus is restored to `restoreFocusRef` (else whatever had
 *     focus when the sheet opened).
 *
 * PORTAL: rendered into document.body, not in place. `position: fixed` is
 * relative to the nearest ancestor with a transform/filter/perspective, and
 * consumers like CustomSelect are routinely mounted inside things that clip
 * or transform (`.modal-content` has `overflow: hidden`) — an in-place sheet
 * would get clipped exactly like NavOrganizer's panel would inside the
 * sidebar. Portaling sidesteps that entirely.
 *
 * ENTRANCE ANIMATION HAS NO fill-mode: a held `animation-fill-mode: forwards`
 * keeps outranking a later plain `style.transform` in the cascade (CSS
 * Animations sit above normal author styles, inline styles included), which
 * would make the drag-to-dismiss transform silently do nothing once the
 * entrance animation "finished but held". Same no-fill-mode choice
 * `.custom-select__dropdown` already makes in ui-components.css.
 *
 * The "should I even show a sheet" viewport check lives in its own
 * useIsNarrowViewport.js, not here — a file that exports a hook alongside a
 * component breaks Vite Fast Refresh (react-refresh/only-export-components).
 */

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

// How far (px) the grip has to be dragged down before a release commits to
// closing instead of springing back. Not a spacing/radius/type value (it's a
// JS drag-physics threshold, not a CSS length), so it isn't token-backed.
const DRAG_CLOSE_THRESHOLD = 120;

/**
 * @param {boolean}  isOpen
 * @param {Function} onClose            Escape, backdrop tap, drag-dismiss, or close button.
 * @param {string}   [title]            Visible heading; also used as the dialog's accessible name.
 * @param {string}   [ariaLabel]        Accessible name when there's no visible `title`.
 * @param {object}   [initialFocusRef]  Ref to focus on open; defaults to the first focusable element.
 * @param {object}   [restoreFocusRef]  Ref to refocus on close; defaults to `document.activeElement` at open time.
 * @param {string}   [className]        Extra class(es) on the sheet panel itself.
 */
export default function BottomSheet({
    isOpen,
    onClose,
    title,
    ariaLabel,
    initialFocusRef,
    restoreFocusRef,
    className = '',
    children,
}) {
    const panelRef = useRef(null);
    const titleId = useId();

    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const dragStartYRef = useRef(null);

    // ── Focus trap: move focus in, cycle Tab inside, restore on close ──
    useEffect(() => {
        if (!isOpen) return undefined;
        const panel = panelRef.current;
        if (!panel) return undefined;

        const previouslyFocused = restoreFocusRef?.current ?? document.activeElement;

        // Everything inside the sheet is visible by construction (it only
        // exists while open), so the selector is the whole filter — same
        // reasoning NavOrganizer's trap documents for its own panel.
        const focusables = () =>
            Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
                .filter(el => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');

        (initialFocusRef?.current ?? focusables()[0])?.focus();

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;
            const list = focusables();
            if (list.length === 0) return;
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        panel.addEventListener('keydown', handleKeyDown);
        return () => {
            panel.removeEventListener('keydown', handleKeyDown);
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, onClose]);

    // A drag left mid-flight (e.g. the sheet closed from elsewhere) shouldn't
    // leave the next open rendering at a stale offset.
    useEffect(() => {
        if (!isOpen) {
            dragStartYRef.current = null;
            setIsDragging(false);
            setDragY(0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleGripPointerDown = (e) => {
        dragStartYRef.current = e.clientY;
        setIsDragging(true);
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const handleGripPointerMove = (e) => {
        if (dragStartYRef.current === null) return;
        setDragY(Math.max(0, e.clientY - dragStartYRef.current));
    };

    const endGripDrag = () => {
        if (dragStartYRef.current === null) return;
        const dragged = dragY;
        dragStartYRef.current = null;
        setIsDragging(false);
        setDragY(0);
        if (dragged > DRAG_CLOSE_THRESHOLD) onClose();
    };

    return createPortal(
        <div
            className="bottom-sheet__overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className={`bottom-sheet ${isDragging ? 'bottom-sheet--dragging' : ''} ${className}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-label={!title ? (ariaLabel || 'Dialog') : undefined}
                ref={panelRef}
                style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
            >
                <div
                    className="bottom-sheet__grip-area"
                    onPointerDown={handleGripPointerDown}
                    onPointerMove={handleGripPointerMove}
                    onPointerUp={endGripDrag}
                    onPointerCancel={endGripDrag}
                >
                    <span className="bottom-sheet__grip" aria-hidden="true" />
                </div>

                {title && (
                    <div className="bottom-sheet__header">
                        <h2 className="bottom-sheet__title" id={titleId}>{title}</h2>
                        <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <X size={18} aria-hidden="true" />
                        </button>
                    </div>
                )}

                <div className="bottom-sheet__body">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
}
